<script setup>
import Locator from 'assets/icons/locator.svg'
import ArrowDown from 'assets/icons/down.svg'

const props = defineProps({
    simpleSelectConfig: {
        type: Object,
        required: true,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                label: rawValue => rawValue?.items === undefined || rawValue.items.every(({ label }) => label === undefined || typeof label === 'string'),
                value: rawValue => rawValue?.items === undefined || rawValue.items.every(({ value }) => value === undefined || typeof value === 'string'),
                selected: rawValue => rawValue?.selected === undefined || typeof rawValue.selected === 'string',
                mobileRendering: rawValue => rawValue?.mobileRendering === undefined || typeof rawValue.mobileRendering === 'boolean'
            }

            return Object.values(validations).every(validate => validate(rawValue))
        }
    }
})

// Offer different styling depending on device
const { isMobile } = useDevice()

const open = ref(false)

const selectedValue = computed(() => {
    const { label } = props.simpleSelectConfig?.items.find(({ value }) => value === props.simpleSelectConfig?.selected) || {}
    return label
})

const getCustomSelectClass = computed(() => {
    return {
        'custom-select-open': open.value
    }
})

/**
 * Check if the item is selected based on simpleSelectConfig.
 *
 * @return {boolean} Whether the item is selected or not.
 */
const isSelected = (value) => {
    return props.simpleSelectConfig?.selected === value
}

/**
 * Toggles the value of the 'open' reactive variable.
 *
 */
const toggleCustomSelect = () => {
    open.value = !open.value
}

/**
 * Updates the current value and toggles the custom select component.
 *
 * @param {any} value - The new value to update to.
 * @return {void}
 */
const handleCustomSelectItemClick = (value) => {
    emit('update:currentValue', value)
    toggleCustomSelect()
}

const emit = defineEmits(['update:currentValue'])
</script>

<template>
    <div
        v-if="isMobile && simpleSelectConfig.mobileRendering"
        class="custom-select custom-select-mobile"
        :class="getCustomSelectClass"
        @click="toggleCustomSelect"
    >
        <Locator />

        <select @change="handleCustomSelectItemClick($event.target?.value)">
            <option
                v-for="item in simpleSelectConfig?.items"
                :key="item.value"
                :value="item.value"
                :selected="isSelected(item.value)"
            >
                {{ item.label }}
            </option>
        </select>

        <ArrowDown class="arrow-icon" />
    </div>

    <div v-else class="custom-select" tabindex="0" :class="getCustomSelectClass">
        <div class="custom-select-item-selected" @click="toggleCustomSelect">
            <Locator />

            {{ selectedValue }}

            <ArrowDown class="arrow-icon" />
        </div>

        <Transition name="slide">
            <div v-if="open" class="custom-select-items">
                <div
                    v-for="item in simpleSelectConfig?.items"
                    :key="item.value"
                    :data-selected="isSelected(item.value)"
                    class="custom-select-item"
                    @click="handleCustomSelectItemClick(item.value)"
                >
                    {{ item.label }}
                </div>
            </div>
        </Transition>
    </div>
</template>

<style lang="scss">
.custom-select {
    display: flex;
    flex-flow: row wrap;
    max-width: 800px;
    margin: 0 auto;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    cursor: pointer;
    position: relative;
    overflow: hidden;

    @include body3-medium;

    &.custom-select-mobile {
        padding: 10px 15px;
        border-radius: 30px;
        background: $custom-select-bg;
    }

    &.custom-select-open  {
        .arrow-icon {
            transform: rotate(-180deg);
        }
    }

    select {
        appearance: none;
        border: none;
        padding: 0 50px;
        margin: 0;
        width: 100%;
        font-family: inherit;
        font-size: inherit;
        cursor: inherit;
        outline: none;
        line-height: inherit;
        background: transparent;
        position: absolute;
        z-index: z('custom-select');
        inset: 0;
        color: $body-text;

        @include body3-medium;

        &::-ms-expand {
            display: none;
        }
    }

    svg {
        max-width: 20px;
        max-height: 20px;
        width: 100%;
        height: 100%;
        position: relative;
        z-index: z('custom-select-icon');

        &.arrow-icon {
            transition: transform 600ms ease-in-out;
        }
    }
}

.custom-select-item-selected {
    padding: 8px 24px;
    cursor: pointer;
    user-select: none;
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    border-radius: 15px;
    background: $custom-select-bg;
    position: relative;
    z-index: z('custom-select-selected');
}

.custom-select-items {
    // @include hide-scrollbar;
    border-radius: 20px;
    background: $custom-select-bg;
    margin-block: 10px 20px;
    width: 100%;
    position: relative;
    z-index: z('custom-select-items');
    max-height: 210px;
    overflow: auto;
}

.custom-select-item {
    cursor: pointer;
    user-select: none;
    padding: 12px 24px;
    text-align: center;
    transition: background-color 400ms ease-in-out;

    &[data-selected="true"] {
        font-weight: bold;
    }

    &:hover {
        background: $custom-select-bg-hover;
    }
}
</style>
